"use client";

import { useEffect, useMemo } from "react";

import {
  ArrowLeftToLine,
  ArrowRightToLine,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";

import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type DataTablePaginationProps = {
  currentPage: number;
  pageCount: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  total?: number;
};

export function DataTablePagination({
  currentPage,
  pageCount,
  setCurrentPage,
  pageSize,
  setPageSize,
  total,
}: DataTablePaginationProps) {
  const pageSizeOptions = useMemo(() => {
    const baseOptions = [10, 20, 25, 40, 50];
    const optionsSet = new Set(baseOptions);

    if (!optionsSet.has(pageSize)) {
      optionsSet.add(pageSize);
    }

    return Array.from(optionsSet).sort((a, b) => a - b);
  }, [pageSize]);

  // When `total` shrinks (a filter narrows results, or rows leave the queue as
  // a curator resolves them), the current page can fall out of range. Clamp to
  // the last page that still holds rows — do NOT reset to page 1, which would
  // eject a curator working the queue back-to-front on every resolution. A
  // partially-full last page is still valid, so only clamp when the page is
  // genuinely empty (its first row index is past `total`).
  useEffect(() => {
    if (total === undefined) return;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (currentPage > lastPage) {
      setCurrentPage(lastPage);
    }
  }, [total, pageSize, currentPage, setCurrentPage]);

  return (
    <Pagination>
      <PaginationContent>
        <PaginationItem>
          <PaginationLink onClick={() => setCurrentPage(1)}>
            <ArrowLeftToLine className="h-4 w-4" />
          </PaginationLink>
        </PaginationItem>
        <PaginationItem aria-disabled={currentPage === 1}>
          <PaginationPrevious
            isActive={currentPage > 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </PaginationPrevious>
        </PaginationItem>
        <PaginationItem>
          <PaginationNext
            isActive={currentPage < pageCount}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            <ChevronRightIcon className="h-4 w-4" />
          </PaginationNext>
        </PaginationItem>
        <PaginationItem>
          <PaginationLink onClick={() => setCurrentPage(pageCount)}>
            <ArrowRightToLine className="h-4 w-4" />
          </PaginationLink>
        </PaginationItem>
        <div className="flex items-center space-x-6 lg:space-x-8">
          <div className="flex items-center justify-center text-sm font-medium">
            Page {currentPage} of {pageCount}
          </div>
          <div className="flex items-center space-x-2">
            <p className="text-sm font-medium">Rows per page</p>
            <Select
              value={`${pageSize}`}
              onValueChange={(value: any) => {
                setPageSize(Number(value));
              }}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue placeholder={pageSize} />
              </SelectTrigger>
              <SelectContent side="top">
                {pageSizeOptions.map((pageSize) => (
                  <SelectItem key={pageSize} value={`${pageSize}`}>
                    {pageSize}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm font-medium">Total Rows {total}</p>
          </div>
        </div>
      </PaginationContent>
    </Pagination>
  );
}

DataTablePagination.displayName = "DataTablePagination";
